<script setup lang="ts">
import LoadingButton from "@/components/LoadingButton.vue"
import Pdf from "@/components/Pdf.vue"
import VarsityContentProcessor from "@/components/VarsityContentProcessor.vue"
import type { ChampionshipRegistrationSelectTeamTask } from "@/model/championship/championship-registration-select-team-task"
import { DownPaymentMode } from "@/model/championship/down-payment-mode"
import { MolliePaymentState } from "@/model/payment/mollie-payment-state"
import type { PaymentMethod } from "@/model/payment/payment-method"
import BookManualPaymentBox from "@/pages/Order/Payment/BookManualPaymentBox.vue"
import MolliePosPaymentBox from "@/pages/Order/Payment/MolliePosPaymentBox.vue"
import MollieRefundBox from "@/pages/Order/Payment/MollieRefundBox.vue"
import PaymentMethodSelect from "@/pages/Order/Payment/PaymentMethodSelect.vue"
import SuccessfulPaymentsList from "@/pages/Order/Payment/SuccessfulPaymentsList.vue"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, $date, $datetime, useApp, useHttpClient, useHttpGet, useLoadingState } from "@/vf"
import { computed, provide, ref, toRefs } from "vue"
import { type ChampionshipRegistrationPayOrderData, SelectTeamDataInjection } from "./championship-registration"
import ChampionshipRegistrationResetButton from "./internal/ChampionshipRegistrationResetButton.vue"
import ChampionshipRegistrationTeamList from "./internal/ChampionshipRegistrationTeamList.vue"
import { useRegistrationPolicy } from "./useRegistrationPolicy"

const props = defineProps<TasksProps<ChampionshipRegistrationSelectTeamTask>>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)
const { data, refresh: refreshData } = await useHttpGet<ChampionshipRegistrationPayOrderData>(
    `/championship/registration/${task.value.organizationId}/pay-order/${task.value.id}`,
)
provide(SelectTeamDataInjection, data)
const http = useHttpClient()
const { apiLink } = useApp()
const policy = useRegistrationPolicy(
    computed(() => data.value?.permissions),
    computed(() => props.viewMode),
)

/*─────────────────────────────────────┐
│  submit                              │
└─────────────────────────────────────*/
const paymentButtonsLoading = useLoadingState()
const allowedPaymentMethods = computed(() => data.value?.allowedPaymentMethods ?? [])
const selectedPaymentMethod = ref<PaymentMethod | null>(null)

// When "partial" is true, only the open down payment configured for the championship is paid
async function pay(partial: boolean) {
    await paymentButtonsLoading.while(async () => {
        const response = await http.post<{ checkoutUrl: string }>(
            `/championship/registration/${data.value!.registrationId}/pay-order?viewMode=${props.viewMode}`,
            { partial, paymentMethod: selectedPaymentMethod.value },
        )

        if (!response.data.checkoutUrl) {
            return
        }

        location.href = response.data.checkoutUrl

        emit("updated")
    })
}

const submitLoading = useLoadingState()

async function submit() {
    await submitLoading.while(async () => {
        await http.post<{ checkoutUrl: string }>(
            `/championship/registration/${data.value!.registrationId}/pay-order?submit=1&viewMode=${props.viewMode}`,
        )

        emit("updated")
    })
}

const pdfKey = ref(0)
async function refresh() {
    await refreshData()

    // Force reload of PDF AFTER the data has been refreshed, because the refreshData call might update the underlying
    // PDF by fetching current payment state from payment provider
    pdfKey.value++
}

async function onTeamListUpdated() {
    await refresh()
    emit("updated")
}

/*─────────────────────────────────────┐
│  Translations                        │
└─────────────────────────────────────*/
const trans = defineTranslations({
    "title": {
        de: "Auftragsbestätigung und Zahlungsaufforderung",
        en: "Order Confirmation and Payment Request",
    },
    "payment_summary": {
        de: "Zahlungsübersicht",
        en: "Payment Summary",
    },
    "total_amount": {
        de: "Gesamtsumme",
        en: "Total Amount",
    },
    "already_paid": {
        de: "Bereits bezahlt",
        en: "Already Paid",
    },
    "open_amount": {
        de: "Offener Betrag",
        en: "Open Amount",
    },
    "payments_info_text.percent": {
        de:
            "Um Euren Startplatz zu sichern, müssen mindestens {percent} % der Teilnahmegebühr bis zum {partial_due_date} angezahlt werden.<br>" +
            "<br>" +
            "Der volle Betrag ist bis spätestens {full_due_date} fällig.<br>" +
            "<br>" +
            "Sobald Eure Zahlung verbucht ist, könnt ihr direkt mit dem nächsten Schritt weitermachen.",
        en:
            "To secure your spot, a minimum payment of {percent} % of the participation fee is due by {partial_due_date}.<br>" +
            "<br>" +
            "The full amount must be paid no later than {full_due_date}." +
            "<br>" +
            "Once your payment has been processed, you can move on to the next step.",
    },
    "payments_info_text.per_team": {
        de:
            "Um Euren Startplatz zu sichern, muss eine Anzahlung von {amount} pro Team bis zum {partial_due_date} geleistet werden.<br>" +
            "<br>" +
            "Der volle Betrag ist bis spätestens {full_due_date} fällig.<br>" +
            "<br>" +
            "Sobald Eure Zahlung verbucht ist, könnt ihr direkt mit dem nächsten Schritt weitermachen.",
        en:
            "To secure your spot, a down payment of {amount} per team is due by {partial_due_date}.<br>" +
            "<br>" +
            "The full amount must be paid no later than {full_due_date}." +
            "<br>" +
            "Once your payment has been processed, you can move on to the next step.",
    },
    "pending_payments.title": {
        de: "Ausstehende Zahlungen",
        en: "Pending Payments",
    },
    "pending_payments.description": {
        de: "Hier stehen alle Zahlungen, die bereits angestoßen wurden, die aber noch nicht abgeschlossen sind.",
        en: "Here you can see all payments you have already initiated, but which are not yet completed.",
    },
    "payment_completed": {
        de: 'Vielen Dank! Die Zahlung wurde erfolgreich vollständig erfasst. Klicke auf "Weiter" um die Anmeldung abzuschließen.',
        en: 'Thank you! The payment has been successfully completed. Click "Continue" to complete the registration.',
    },
    "payment_status.paid": {
        de: "Zahlung erfolgt",
        en: "Payment completed",
    },
    "payment_status.pending": {
        de: "Zahlung wird verarbeitet",
        en: "Payment is being processed",
    },
    "payment_status.open": {
        de: "Zahlung wurde angefragt",
        en: "Payment requested",
    },
    "payment_status.expired": {
        de: "Zahlung abgelaufen",
        en: "Payment expired",
    },
    "payment_status.canceled": {
        de: "Zahlung abgebrochen",
        en: "Payment canceled",
    },
    "payment_status.prefix_date": {
        de: "Zahlung vom",
        en: "Payment from",
    },
    "action.refresh": {
        de: "Aktualisieren",
        en: "Refresh",
    },
    "action.pay_now_fully": {
        de: "Jetzt vollständig bezahlen ({amount})",
        en: "Pay in full now ({amount})",
    },
    "action.pay_now_partial": {
        de: "Jetzt Teilzahlung leisten ({amount})",
        en: "Make partial payment now ({amount})",
    },
    "action.complete_registration": {
        de: "Anmeldung abschließen",
        en: "Complete registration",
    },
    "banktransfer.description": {
        de: "Bitte überweise den Betrag ausschließlich auf folgendes Konto:",
        en: "Please transfer the amount to the following account:",
    },
    "banktransfer.expiration_hint": {
        de: "Bitte beachte, dass die Zahlung bis zum {expiresAt} bei unserem Zahlungsdienstleister eingegangen sein muss und der Betrag exakt übereinstimmen muss. Ansonsten wird die Zahlung automatisch storniert und du musst hier eine neue Zahlung anstoßen.",
        en: "Please note that the payment must be received by our payment service provider by {expiresAt} and the amount must match exactly. Otherwise, the payment will be automatically canceled and you will need to initiate a new payment here.",
    },
    "banktransfer.recipient": {
        de: "Begünstigter",
        en: "Recipient",
    },
    "banktransfer.iban": {
        de: "IBAN",
        en: "IBAN",
    },
    "banktransfer.bic": {
        de: "BIC",
        en: "BIC",
    },
    "banktransfer.amount": {
        de: "Betrag",
        en: "Amount",
    },
    "banktransfer.reference": {
        de: "Verwendungszweck",
        en: "Transfer reference",
    },
    "payment_method.creditcard": {
        de: "Kreditkarte",
        en: "Credit Card",
    },
    "payment_method.paybybank": {
        de: "Sofortüberweisung",
        en: "Pay by Bank",
    },
    "payment_method.banktransfer": {
        de: "Banküberweisung",
        en: "Bank Transfer",
    },
    "payment_method.manual": {
        de: "Manuell gebucht",
        en: "Manually booked",
    },
    "successful_payments.title": {
        de: "Erfolgreiche Zahlungen",
        en: "Successful Payments",
    },
})

function translatePaymentMethod(method: string) {
    switch (method) {
        case "creditcard":
            return trans("payment_method.creditcard")
        case "paybybank":
            return trans("payment_method.paybybank")
        case "banktransfer":
            return trans("payment_method.banktransfer")
        default:
            return method
    }
}
</script>
<template>
    <div class="p-5">
        <!-- <div class="p-5" v-if="props.viewMode === 'admin'"> -->
        <!--     <div class="alert alert-info"> -->
        <!--         {{ $t("@tasks:tasks.championship_registration.select_payment_method.waiting_for_customer") }} -->
        <!--     </div> -->
        <!---->
        <!--     <ChampionshipRegistrationTeamList :teams="data!.teams" :view-mode="props.viewMode" /> -->
        <!-- </div> -->
        <!-- <div class="p-5" v-else> -->
        <b>{{ $t("@tasks:tasks.championship_registration.select_payment_method.description.title") }}</b>
        <div>{{ $t("@tasks:tasks.championship_registration.select_payment_method.description.text") }}</div>

        <ChampionshipRegistrationTeamList
            :registration-id="data!.registrationId"
            :teams="data!.teams"
            :view-mode="props.viewMode"
            :show-edit="true"
            :permissions="data?.permissions"
            @updated="onTeamListUpdated"
        />
        <ChampionshipRegistrationResetButton
            :registrationId="data!.registrationId"
            :view-mode="props.viewMode"
            v-if="policy.ResetRegistration"
            @resetted="emit('updated')"
            #default="{ renderButton, renderConfirm }"
        >
            <div class="text-right">
                <component :is="renderButton" />
            </div>
            <div>
                <component :is="renderConfirm" />
            </div>
        </ChampionshipRegistrationResetButton>
        <h2 class="mt-5 mb-3">
            {{ trans("title") }}
        </h2>
        <div class="row">
            <div class="col-xl-7 col-lg-6">
                <Pdf
                    :file="`/championship/registration/${data!.registrationId}/latest-order-confirmation`"
                    :key="pdfKey"
                />
            </div>

            <div class="col-xl-5 col-lg-6">
                <div class="mb-5" v-if="policy.CreateOrderPayment">
                    <VarsityContentProcessor
                        allow-all-tags
                        :content="
                            data?.downPaymentMode === DownPaymentMode.PerTeam
                                ? trans('payments_info_text.per_team', {
                                      amount: $currency(data.downPaymentPerTeamCents ?? 0),
                                      partial_due_date: $date(data.partialPaymentDueDate),
                                      full_due_date: $date(data.fullPaymentDueDate),
                                  })
                                : trans('payments_info_text.percent', {
                                      percent: data?.downPaymentPercent ?? 0,
                                      partial_due_date: $date(data?.partialPaymentDueDate),
                                      full_due_date: $date(data?.fullPaymentDueDate),
                                  })
                        "
                    />
                </div>

                <div v-if="policy.SubmitPaidOrder" class="mb-5">
                    <div class="alert alert-success">
                        {{ trans("payment_completed") }}
                    </div>
                    <LoadingButton :loading="submitLoading" class="btn btn-primary" @click="submit()">
                        {{ trans("action.complete_registration") }}
                    </LoadingButton>
                </div>

                <div class="bg-primary-dark text-white px-4 py-2 font-weight-bold">
                    {{ trans("payment_summary") }}
                </div>

                <table class="table table-borderless">
                    <tbody>
                        <tr>
                            <th>
                                {{ trans("total_amount") }}
                            </th>
                            <td class="text-right">
                                {{ $currency(data!.totalAmount, 100, { currencyDisplay: "name" }) }}
                            </td>
                        </tr>
                        <tr>
                            <th>
                                {{ trans("already_paid") }}
                            </th>
                            <td class="text-right">
                                {{ $currency(data!.paidAmount, 100, { currencyDisplay: "name" }) }}
                            </td>
                        </tr>
                        <tr>
                            <td colspan="2" class="text-center py-0"><hr class="my-1" /></td>
                        </tr>
                        <tr>
                            <th>
                                {{ trans("open_amount") }}
                            </th>
                            <td class="text-right">
                                {{ $currency(data!.unpaidAmount, 100, { currencyDisplay: "name" }) }}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div v-if="data?.successfulPayments?.length" class="mt-4">
                    <b>{{ trans("successful_payments.title") }}</b>
                    <SuccessfulPaymentsList
                        :payments="data.successfulPayments"
                        :view-mode="props.viewMode"
                        class="mt-2"
                    />
                </div>

                <template v-if="props.task.state === 'open'">
                    <div v-if="data?.pendingPayments?.length && data.unpaidAmount > 0" class="mt-4">
                        <b>{{ trans("pending_payments.title") }}</b>
                        <p>{{ trans("pending_payments.description") }}</p>
                        <div class="list-group rounded">
                            <div v-for="payment in data.pendingPayments" :key="payment.id" class="list-group-item">
                                <div class="d-flex justify-content-between align-items-center">
                                    <div class="mr-3">
                                        <i
                                            class="fa fa-fw fa-check text-success"
                                            v-if="payment.state === MolliePaymentState.Paid"
                                        ></i>
                                        <i
                                            class="fa fa-fw fa-times text-danger"
                                            v-else-if="
                                                payment.state === MolliePaymentState.Canceled ||
                                                payment.state === MolliePaymentState.Expired
                                            "
                                        ></i>
                                        <i class="far fa-fw fa-clock" v-else></i>
                                    </div>
                                    <div class="flex-grow-1">
                                        <div class="text-success" v-if="payment.state === MolliePaymentState.Paid">
                                            {{ trans("payment_status.paid") }}
                                        </div>
                                        <div class="" v-else-if="payment.state === MolliePaymentState.Pending">
                                            {{ trans("payment_status.pending") }}
                                        </div>
                                        <div class="text-success" v-else-if="payment.state === MolliePaymentState.Open">
                                            {{ trans("payment_status.open") }}
                                        </div>
                                        <div
                                            class="text-danger"
                                            v-else-if="payment.state === MolliePaymentState.Expired"
                                        >
                                            {{ trans("payment_status.expired") }}
                                        </div>
                                        <div
                                            class="text-danger"
                                            v-else-if="payment.state === MolliePaymentState.Canceled"
                                        >
                                            {{ trans("payment_status.canceled") }}
                                        </div>
                                        <div class="text-muted small">
                                            <template v-if="payment.paymentMethod">
                                                {{ translatePaymentMethod(payment.paymentMethod) }}
                                                &nbsp;·&nbsp;
                                            </template>
                                            {{ trans("payment_status.prefix_date") }} {{ $datetime(payment.createdAt) }}
                                        </div>
                                    </div>

                                    <div class="badge badge-primary badge-pill text-white">
                                        {{ $currency(payment.amount, 100, { currencyDisplay: "name" }) }}
                                    </div>
                                </div>
                                <div
                                    v-if="
                                        payment.paymentMethod === 'banktransfer' &&
                                        payment.paymentDetails &&
                                        payment.state === MolliePaymentState.Open
                                    "
                                    class="mt-4"
                                >
                                    <hr />
                                    {{ trans("banktransfer.description") }}

                                    <table class="table table-borderless">
                                        <tbody>
                                            <tr>
                                                <th>{{ trans("banktransfer.recipient") }}</th>
                                                <td>{{ payment.paymentDetails.bankName }}</td>
                                            </tr>
                                            <tr>
                                                <th>{{ trans("banktransfer.iban") }}</th>
                                                <td>{{ payment.paymentDetails.bankAccount }}</td>
                                            </tr>
                                            <tr>
                                                <th>{{ trans("banktransfer.bic") }}</th>
                                                <td>{{ payment.paymentDetails.bankBic }}</td>
                                            </tr>
                                            <tr>
                                                <th>{{ trans("banktransfer.reference") }}</th>
                                                <td>{{ payment.paymentDetails.transferReference }}</td>
                                            </tr>
                                            <tr>
                                                <th>{{ trans("banktransfer.amount") }}</th>
                                                <td>{{ $currency(payment.amount!) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <p class="mt-4" v-if="payment.expiresAt">
                                        {{
                                            trans("banktransfer.expiration_hint", {
                                                expiresAt: $date(payment.expiresAt),
                                            })
                                        }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="mt-3">
                            <button class="btn btn-primary" type="button" @click="refresh">
                                {{ trans("action.refresh") }}
                            </button>
                        </div>
                    </div>

                    <div v-if="(data?.unpaidAmount ?? 0) > 0 && policy.CreateOrderPayment">
                        <PaymentMethodSelect
                            v-if="allowedPaymentMethods.length"
                            v-model="selectedPaymentMethod"
                            :methods="allowedPaymentMethods"
                            class="mt-4"
                        />

                        <div class="text-right">
                            <hr />
                            <LoadingButton
                                :loading="paymentButtonsLoading"
                                class="btn btn-primary mt-3 ml-auto"
                                @click="pay(true)"
                                v-if="(data?.partialPaymentUnpaid ?? 0) > 0"
                                :disabled="allowedPaymentMethods.length > 0 && !selectedPaymentMethod"
                            >
                                {{
                                    trans("action.pay_now_partial", {
                                        amount: $currency(data?.partialPaymentUnpaid),
                                    })
                                }}
                            </LoadingButton>

                            <LoadingButton
                                :loading="paymentButtonsLoading"
                                class="btn btn-primary mt-3 ml-auto"
                                @click="pay(false)"
                                v-if="(data?.unpaidAmount ?? 0) > 0"
                                :disabled="allowedPaymentMethods.length > 0 && !selectedPaymentMethod"
                            >
                                {{ trans("action.pay_now_fully", { amount: $currency(data?.unpaidAmount) }) }}
                            </LoadingButton>
                        </div>
                    </div>
                </template>

                <MollieRefundBox
                    class="my-5 bg-white"
                    v-if="policy.CreateRefund && data?.registrationId"
                    :data-endpoint="`/championship/registration/${data?.registrationId}/refund-data?viewMode=${props.viewMode}`"
                    :refund-endpoint="`/championship/registration/${data?.registrationId}/refund?viewMode=${props.viewMode}`"
                    @refunded="refresh"
                />

                <MolliePosPaymentBox
                    class="my-5 bg-white"
                    v-if="data?.orderProcessId && data?.registrationId && policy.CreatePosPayment"
                    :order-process-id="data.orderProcessId"
                    :data-endpoint="`/championship/registration/${data.registrationId}/pos-data?viewMode=${props.viewMode}`"
                    :create-endpoint="`/championship/registration/${data.registrationId}/pos-payment?viewMode=${props.viewMode}`"
                    @paid="refresh"
                />

                <BookManualPaymentBox
                    v-if="policy.BookManualPayment"
                    class="bg-white mt-5"
                    :endpoint="`/championship/registration/${data!.registrationId}/book-manual-payment?viewMode=${props.viewMode}`"
                    @booked="refresh"
                />
            </div>
        </div>

        <div class="row">
            <div class="col-xl-7 col-lg-6">
                <div class="text-right mt-5">
                    <a
                        :href="
                            apiLink(
                                `/championship/registration/${data?.registrationId}/latest-order-confirmation?download=1`,
                            )
                        "
                        class="btn btn-primary mb-0 text-nowrap"
                        v-if="data?.hasMolliePayments"
                    >
                        <i class="fa fa-fw fa-download"></i>
                        Download
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>
